import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Calendar, Users, Mail, Phone, User, ChevronRight, ChevronLeft, Check, Loader2, BedDouble, AlertCircle, Shield, Clock, FileText, Star, Minus, Plus, CreditCard } from "lucide-react";
import axios from "axios";

// Use the origin the widget is loaded from (handles custom domains like csyncpro.com)
const API = `${window.location.origin}/api`;

const STEP_LABELS = ["Dates", "Room", "Details", "Confirm"];

export default function EmbedBookingWidget() {
  const { embedToken } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [checking, setChecking] = useState(false);
  const [booking, setBooking] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [rates, setRates] = useState({});
  const [roomImages, setRoomImages] = useState({});
  const [activePhoto, setActivePhoto] = useState({});
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [holdId, setHoldId] = useState(null);
  const [holdExpiry, setHoldExpiry] = useState(null);
  const [holdCountdown, setHoldCountdown] = useState(null);

  const [form, setForm] = useState({
    startDate: "", endDate: "", roomTypeId: "", roomTypeName: "",
    adults: 2, children: 0, firstName: "", lastName: "",
    email: "", phone: "", specialRequests: ""
  });

  useEffect(() => {
    axios.get(`${API}/embed/${embedToken}/config`)
      .then(res => { setConfig(res.data); setLoading(false); })
      .catch(() => { setError("This booking engine is not available."); setLoading(false); });
    // Check for payment return from Fiserv
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get("payment");
    const paymentRef = urlParams.get("ref");
    if (paymentStatus === "success" && paymentRef) {
      const cardBrand = urlParams.get("card") || "";
      const cardLast4 = urlParams.get("last4") || "";
      const approvalCode = urlParams.get("approval") || "";
      const paidAmount = urlParams.get("amount") || "";
      const bookingRef = urlParams.get("bref") || paymentRef;
      const txnId = urlParams.get("txnid") || "";
      const refNum = urlParams.get("refnum") || "";
      setBookingResult({
        booking_ref: bookingRef,
        reservation_id: "",
        guest_name: "",
        check_in: "",
        check_out: "",
        nights: 0,
        message: "Payment successful! Your booking is confirmed.",
        payment: {
          card_brand: cardBrand,
          card_last4: cardLast4,
          approval_code: approvalCode,
          amount: paidAmount,
          transaction_id: txnId,
          ref_number: refNum,
          order_id: paymentRef
        }
      });
      setStep(4);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (paymentStatus === "failed") {
      const reason = urlParams.get("reason") || "Payment was not completed";
      setError(`Payment failed: ${reason}. Please try again.`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [embedToken]);

  useEffect(() => {
    if (!holdExpiry) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.floor((new Date(holdExpiry) - new Date()) / 1000));
      setHoldCountdown(remaining);
      if (remaining <= 0) { clearInterval(interval); setHoldId(null); setHoldExpiry(null); setError("Your booking hold has expired. Please start again."); setStep(0); }
    }, 1000);
    return () => clearInterval(interval);
  }, [holdExpiry]);

  const settings = config?.settings || {};
  const bookingSettings = config?.booking_settings || {};
  const taxFees = bookingSettings.tax_fees || [];
  const currency = bookingSettings.currency || "USD";
  const sym = currency === "USD" ? "$" : currency + " ";

  const checkAvailability = useCallback(async () => {
    if (!form.startDate || !form.endDate) return;
    if (new Date(form.endDate) <= new Date(form.startDate)) { setError("Check-out must be after check-in"); return; }
    setChecking(true); setError(null);
    try {
      const res = await axios.get(`${API}/embed/${embedToken}/availability`, { params: { start_date: form.startDate, end_date: form.endDate } });
      let flatRooms = [];
      for (const item of (res.data.rooms || [])) {
        if (item.propertyRooms) flatRooms = [...flatRooms, ...item.propertyRooms];
        else flatRooms.push(item);
      }
      setRooms(flatRooms);
      setRates(res.data.rates || {});
      setRoomImages(res.data.room_images || {});
      setStep(1);
    } catch (e) { setError(e.response?.data?.detail || "Unable to check availability."); }
    setChecking(false);
  }, [embedToken, form.startDate, form.endDate]);

  const selectRoom = useCallback(async (roomId, roomName) => {
    setForm(f => ({ ...f, roomTypeId: roomId, roomTypeName: roomName }));
    setCalculatingPrice(true);
    try {
      const res = await axios.post(`${API}/embed/${embedToken}/calculate-price`, { room_type_id: roomId, start_date: form.startDate, end_date: form.endDate });
      setPriceBreakdown(res.data.breakdown);
    } catch { }
    setCalculatingPrice(false);
  }, [embedToken, form.startDate, form.endDate]);

  const createHold = async () => {
    try {
      const res = await axios.post(`${API}/embed/${embedToken}/hold`, { room_type_id: form.roomTypeId, start_date: form.startDate, end_date: form.endDate, guest_email: form.email });
      setHoldId(res.data.hold_id); setHoldExpiry(res.data.expires_at);
    } catch { }
  };

  const submitBooking = async () => {
    setBooking(true); setError(null);
    try {
      const res = await axios.post(`${API}/embed/${embedToken}/book`, {
        room_type_id: form.roomTypeId, start_date: form.startDate, end_date: form.endDate,
        guest_first_name: form.firstName, guest_last_name: form.lastName, guest_email: form.email,
        guest_phone: form.phone || undefined, adults: form.adults, children: form.children, special_requests: form.specialRequests || undefined
      });

      // Check if property uses Fiserv or CyberSource — redirect to hosted payment page
      const processor = bookingSettings.payment_processor;
      if ((processor === "fiserv" || processor === "cybersource") && priceBreakdown && priceBreakdown.grand_total > 0) {
        try {
          const initEndpoint = processor === "fiserv" ? "fiserv" : "cybersource";
          const payRes = await axios.post(`${API}/payment/${initEndpoint}/init`, {
            embed_token: embedToken,
            booking_ref: res.data.booking_ref || res.data.reservation_id,
            amount: priceBreakdown.grand_total,
            guest_name: `${form.firstName} ${form.lastName}`,
            guest_email: form.email,
            order_description: `${form.roomTypeName} - ${form.startDate} to ${form.endDate}`
          });
          if (payRes.data.success) {
            // Redirect to our server's auto-submit page which POSTs to the gateway
            // This properly breaks out of iframe since it's a same-origin navigation first
            const redirectUrl = payRes.data.redirect_url;
            if (window.self !== window.top) {
              window.top.location.href = redirectUrl;
            } else {
              window.location.href = redirectUrl;
            }
            return;
          }
        } catch (payErr) {
          // Payment init failed — still show booking confirmation (payment can be collected later)
          console.error(`${processor} payment init failed:`, payErr);
        }
      }

      // No payment processor or Fiserv not configured — show direct confirmation
      setBookingResult(res.data);
      if (res.data.breakdown) setPriceBreakdown(res.data.breakdown);
      setStep(4); setHoldExpiry(null);
    } catch (e) { setError(e.response?.data?.detail || "Booking failed. Please try again."); }
    setBooking(false);
  };

  const nightCount = form.startDate && form.endDate ? Math.max(1, Math.ceil((new Date(form.endDate) - new Date(form.startDate)) / 86400000)) : 0;
  const today = new Date().toISOString().split("T")[0];
  const formatHold = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (loading) return <div className="ebw-loading"><Loader2 className="ebw-spin" /></div>;
  if (error && !config) return <div className="ebw-error-page"><AlertCircle /><p>{error}</p></div>;

  const PriceSummaryCard = () => priceBreakdown ? (
    <div className="ebw-price-card" data-testid="price-summary-card">
      <div className="ebw-price-card-header">
        <span className="ebw-price-label">Price Summary</span>
        <span className="ebw-price-nights">{priceBreakdown.nights} night{priceBreakdown.nights !== 1 ? "s" : ""}</span>
      </div>
      <div className="ebw-price-lines">
        <div className="ebw-price-line"><span>Room rate</span><span>{sym}{priceBreakdown.subtotal.toFixed(2)}</span></div>
        {priceBreakdown.tax_fee_lines.map((l, i) => (
          <div key={i} className="ebw-price-line ebw-price-tax"><span>{l.label}{l.type === "percentage" ? ` (${l.rate}%)` : ""}</span><span>{sym}{l.amount.toFixed(2)}</span></div>
        ))}
      </div>
      <div className="ebw-price-total"><span>Total</span><span>{sym}{priceBreakdown.grand_total.toFixed(2)}</span></div>
      {bookingSettings.payment_mode === "deposit" && (
        <div className="ebw-price-deposit"><span>Due now ({bookingSettings.deposit_percentage}%)</span><span>{sym}{priceBreakdown.amount_due.toFixed(2)}</span></div>
      )}
    </div>
  ) : null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Manrope:wght@300;400;500;600;700&display=swap');
        .ebw{font-family:'Manrope',sans-serif;background:#FAF9F6;min-height:100vh;color:#1C1C1C;-webkit-font-smoothing:antialiased}
        .ebw *{box-sizing:border-box}
        .ebw-serif{font-family:'Cormorant Garamond',Georgia,serif}
        .ebw-header{background:#1A2421;padding:20px 24px;display:flex;align-items:center;justify-content:space-between}
        .ebw-header h1{color:#fff;font-family:'Cormorant Garamond',serif;font-size:22px;font-weight:500;margin:0;letter-spacing:-0.5px}
        .ebw-header p{color:rgba(255,255,255,0.5);font-size:13px;margin:4px 0 0}
        .ebw-header-badge{display:flex;align-items:center;gap:6px;color:#C2A878;font-size:11px;text-transform:uppercase;letter-spacing:2px;font-weight:600}
        .ebw-progress{display:flex;align-items:center;padding:16px 24px;background:#fff;border-bottom:1px solid #E5E4DF}
        .ebw-step{display:flex;align-items:center;gap:8px;flex:1;min-width:0}
        .ebw-step-num{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;flex-shrink:0;transition:all .3s}
        .ebw-step-label{font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:1.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .ebw-step-line{width:20px;height:1px;background:#E5E4DF;flex-shrink:0;margin:0 2px}
        .ebw-hold{display:flex;align-items:center;gap:6px;padding:8px 24px;font-size:12px;font-weight:600;color:#C2A878;background:#FAF9F6;border-bottom:1px solid #E5E4DF}
        .ebw-body{max-width:1200px;margin:0 auto;padding:24px}
        .ebw-2col{display:grid;grid-template-columns:1fr;gap:24px}
        @media(min-width:860px){.ebw-2col{grid-template-columns:7fr 5fr}}
        .ebw-card{background:#fff;border:1px solid #E5E4DF;border-radius:6px;padding:24px}
        .ebw-card-title{font-family:'Cormorant Garamond',serif;font-size:22px;font-weight:500;margin:0 0 20px;letter-spacing:-0.3px}
        .ebw-label{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:1.5px;color:#575757;margin-bottom:8px;display:block}
        .ebw-input{width:100%;height:56px;border:1px solid #E5E4DF;border-radius:4px;padding:0 16px;font-size:15px;font-family:'Manrope',sans-serif;transition:border .2s;background:#fff;outline:none}
        .ebw-input:focus{border-color:#1A2421;box-shadow:0 0 0 1px #1A2421}
        .ebw-input-icon{position:relative}.ebw-input-icon svg{position:absolute;left:14px;top:50%;transform:translateY(-50%);color:#999;width:18px;height:18px}
        .ebw-input-icon .ebw-input{padding-left:44px}
        .ebw-counter{display:flex;align-items:center;height:56px;border:1px solid #E5E4DF;border-radius:4px;overflow:hidden}
        .ebw-counter button{width:52px;height:100%;display:flex;align-items:center;justify-content:center;background:none;border:none;cursor:pointer;color:#575757;font-size:18px;transition:background .2s}
        .ebw-counter button:hover{background:#F5F4F0}
        .ebw-counter span{flex:1;text-align:center;font-size:16px;font-weight:600}
        .ebw-grid2{display:grid;grid-template-columns:1fr 1fr;gap:16px}
        .ebw-btn{width:100%;height:56px;border:none;border-radius:4px;font-size:14px;font-weight:700;font-family:'Manrope',sans-serif;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .3s;text-transform:uppercase;letter-spacing:1px}
        .ebw-btn-primary{background:#1A2421;color:#fff}.ebw-btn-primary:hover{background:#2C3E35;transform:translateY(-1px)}
        .ebw-btn-accent{background:#C2A878;color:#fff}.ebw-btn-accent:hover{background:#B39868;transform:translateY(-1px)}
        .ebw-btn:disabled{opacity:.4;cursor:not-allowed;transform:none}
        .ebw-btn-outline{background:transparent;border:2px solid #1A2421;color:#1A2421}.ebw-btn-outline:hover{background:#1A2421;color:#fff}
        .ebw-back{display:inline-flex;align-items:center;gap:4px;font-size:13px;font-weight:600;color:#575757;cursor:pointer;background:none;border:none;padding:0;margin-bottom:16px;font-family:'Manrope',sans-serif}
        .ebw-back:hover{color:#1A2421}
        .ebw-room{background:#fff;border:1px solid #E5E4DF;border-radius:6px;overflow:hidden;cursor:pointer;transition:all .3s}
        .ebw-room:hover{transform:translateY(-3px);box-shadow:0 12px 40px rgba(0,0,0,0.08)}
        .ebw-room.selected{border-color:#1A2421;box-shadow:0 0 0 1px #1A2421}
        .ebw-room-img{position:relative;width:100%;height:220px;background:#E5E4DF;overflow:hidden}
        .ebw-room-img img{width:100%;height:100%;object-fit:cover;transition:transform .4s}
        .ebw-room:hover .ebw-room-img img{transform:scale(1.05)}
        .ebw-room-nav{position:absolute;top:50%;transform:translateY(-50%);width:32px;height:32px;border-radius:50%;background:rgba(0,0,0,0.4);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#fff;transition:background .2s}
        .ebw-room-nav:hover{background:rgba(0,0,0,0.7)}
        .ebw-room-nav.left{left:8px}.ebw-room-nav.right{right:8px}
        .ebw-room-price{position:absolute;top:12px;right:12px;background:rgba(255,255,255,0.95);backdrop-filter:blur(8px);padding:6px 14px;border-radius:4px;font-weight:800;font-size:18px;color:#1A2421}
        .ebw-room-price small{font-size:11px;font-weight:500;color:#575757}
        .ebw-room-dots{position:absolute;bottom:10px;left:50%;transform:translateX(-50%);display:flex;gap:4px}
        .ebw-room-dot{width:6px;height:6px;border-radius:50%;background:rgba(255,255,255,0.4)}.ebw-room-dot.active{background:#fff}
        .ebw-room-badges{position:absolute;top:12px;left:12px;display:flex;gap:6px}
        .ebw-room-badge{background:rgba(194,168,120,0.9);backdrop-filter:blur(4px);color:#fff;font-size:10px;font-weight:700;padding:4px 10px;border-radius:2px;text-transform:uppercase;letter-spacing:1px}
        .ebw-room-info{padding:20px}
        .ebw-room-name{font-family:'Cormorant Garamond',serif;font-size:20px;font-weight:600;margin:0 0 6px}
        .ebw-room-desc{font-size:13px;color:#575757;line-height:1.6;margin:0 0 10px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
        .ebw-room-meta{display:flex;gap:16px;font-size:12px;color:#575757}
        .ebw-room-avail{color:#963c32;font-weight:700}
        .ebw-room-selected{display:flex;align-items:center;gap:6px;margin-top:10px;font-size:12px;font-weight:700;color:#1A2421}
        .ebw-price-card{background:#fff;border:1px solid #E5E4DF;border-radius:6px;padding:24px;position:sticky;top:24px}
        .ebw-price-card-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:16px}
        .ebw-price-label{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:1.5px;color:#C2A878}
        .ebw-price-nights{font-size:12px;color:#575757}
        .ebw-price-lines{border-top:1px solid #E5E4DF;padding-top:12px}
        .ebw-price-line{display:flex;justify-content:space-between;padding:6px 0;font-size:14px}
        .ebw-price-line span:last-child{font-weight:600;font-variant-numeric:tabular-nums}
        .ebw-price-tax{color:#575757;font-size:13px}
        .ebw-price-total{display:flex;justify-content:space-between;padding:16px 0 0;margin-top:8px;border-top:2px solid #1A2421;font-size:18px;font-weight:800}
        .ebw-price-deposit{display:flex;justify-content:space-between;padding:10px 12px;margin-top:10px;background:#FAF9F6;border-radius:4px;font-size:13px;font-weight:700;color:#C2A878}
        .ebw-trust{display:flex;align-items:center;justify-content:center;gap:8px;padding:12px;font-size:11px;color:#999;text-transform:uppercase;letter-spacing:1px}
        .ebw-summary-row{display:flex;justify-content:space-between;padding:8px 0;font-size:14px;border-bottom:1px solid #F5F4F0}
        .ebw-summary-row span:first-child{color:#575757}
        .ebw-summary-row span:last-child{font-weight:600}
        .ebw-policy{display:flex;gap:10px;padding:14px;background:#F5F4F0;border-radius:4px;font-size:13px;color:#575757;margin-top:16px}
        .ebw-policy svg{flex-shrink:0;color:#C2A878;width:16px;height:16px;margin-top:2px}
        .ebw-success-icon{width:64px;height:64px;border-radius:50%;background:#3B5249;display:flex;align-items:center;justify-content:center;margin:0 auto 20px}
        .ebw-success-icon svg{color:#fff;width:32px;height:32px}
        .ebw-footer{text-align:center;padding:16px;font-size:11px;color:#ccc;letter-spacing:1px}
        .ebw-footer a{color:#999;text-decoration:underline}
        .ebw-loading{display:flex;align-items:center;justify-content:center;min-height:100vh;background:#FAF9F6}
        .ebw-spin{width:32px;height:32px;color:#C2A878;animation:ebw-rotate 1s linear infinite}
        @keyframes ebw-rotate{to{transform:rotate(360deg)}}
        .ebw-error-page{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;background:#FAF9F6;text-align:center;padding:24px}
        .ebw-error-page svg{width:40px;height:40px;color:#963c32;margin-bottom:12px}
        .ebw-error-banner{margin:0 0 16px;padding:12px 16px;background:#fef2f2;border:1px solid #fecaca;border-radius:4px;display:flex;align-items:start;gap:8px;font-size:13px;color:#963c32}
        .ebw-error-banner svg{flex-shrink:0;width:16px;height:16px;margin-top:1px}
        .ebw-error-banner button{margin-left:auto;background:none;border:none;font-size:18px;cursor:pointer;color:#963c32;padding:0;line-height:1}
        .ebw-textarea{width:100%;border:1px solid #E5E4DF;border-radius:4px;padding:16px;font-size:15px;font-family:'Manrope',sans-serif;resize:none;outline:none;transition:border .2s}
        .ebw-textarea:focus{border-color:#1A2421;box-shadow:0 0 0 1px #1A2421}
        @media(max-width:859px){
          .ebw-body{padding:16px}
          .ebw-card{padding:20px}
          .ebw-room-img{height:180px}
          .ebw-header h1{font-size:19px}
        }
        ${settings.custom_css || ""}
      `}</style>
      <div className="ebw">
        {/* Header */}
        <div className="ebw-header">
          <div>
            {settings.logo_url && <img src={settings.logo_url} alt="" style={{ height: 28, marginBottom: 4, objectFit: "contain" }} />}
            <h1 data-testid="embed-property-name">{settings.title || config?.property_name}</h1>
            <p>{settings.description}</p>
          </div>
          <div className="ebw-header-badge"><Shield style={{ width: 14, height: 14 }} /> Direct Booking</div>
        </div>

        {/* Progress */}
        {step < 4 && (
          <div className="ebw-progress">
            {STEP_LABELS.map((label, i) => (
              <div key={label} className="ebw-step">
                <div className="ebw-step-num" style={{ background: i < step ? "#1A2421" : i === step ? "#C2A878" : "#E5E4DF", color: i <= step ? "#fff" : "#999" }} data-testid={`step-indicator-${i}`}>
                  {i < step ? <Check style={{ width: 14, height: 14 }} /> : `0${i + 1}`}
                </div>
                <span className="ebw-step-label" style={{ color: i <= step ? "#1A2421" : "#999" }}>{label}</span>
                {i < 3 && <div className="ebw-step-line" style={{ background: i < step ? "#1A2421" : "#E5E4DF" }} />}
              </div>
            ))}
          </div>
        )}

        {holdCountdown != null && holdCountdown > 0 && step >= 2 && (
          <div className="ebw-hold"><Clock style={{ width: 14, height: 14 }} /> Room held for {formatHold(holdCountdown)}</div>
        )}

        {error && step < 4 && (
          <div style={{ padding: "0 24px", marginTop: 16 }}>
            <div className="ebw-error-banner"><AlertCircle /><span style={{ flex: 1 }}>{error}</span><button onClick={() => setError(null)}>&times;</button></div>
          </div>
        )}

        <div className="ebw-body">
          {/* STEP 0: Dates */}
          {step === 0 && (
            <div className="ebw-2col" data-testid="step-dates">
              <div className="ebw-card">
                <h2 className="ebw-card-title">When are you staying?</h2>
                <div className="ebw-grid2">
                  <div>
                    <label className="ebw-label">Check-in</label>
                    <input type="date" min={today} value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} className="ebw-input" data-testid="check-in-date" />
                  </div>
                  <div>
                    <label className="ebw-label">Check-out</label>
                    <input type="date" min={form.startDate || today} value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} className="ebw-input" data-testid="check-out-date" />
                  </div>
                </div>
                <div className="ebw-grid2" style={{ marginTop: 16 }}>
                  <div>
                    <label className="ebw-label">Adults</label>
                    <div className="ebw-counter">
                      <button onClick={() => setForm(f => ({ ...f, adults: Math.max(1, f.adults - 1) }))} data-testid="adults-minus"><Minus style={{ width: 16, height: 16 }} /></button>
                      <span data-testid="adults-count">{form.adults}</span>
                      <button onClick={() => setForm(f => ({ ...f, adults: Math.min(settings.max_guests || 10, f.adults + 1) }))} data-testid="adults-plus"><Plus style={{ width: 16, height: 16 }} /></button>
                    </div>
                  </div>
                  <div>
                    <label className="ebw-label">Children</label>
                    <div className="ebw-counter">
                      <button onClick={() => setForm(f => ({ ...f, children: Math.max(0, f.children - 1) }))}><Minus style={{ width: 16, height: 16 }} /></button>
                      <span>{form.children}</span>
                      <button onClick={() => setForm(f => ({ ...f, children: Math.min(settings.max_guests || 10, f.children + 1) }))}><Plus style={{ width: 16, height: 16 }} /></button>
                    </div>
                  </div>
                </div>
                <button onClick={checkAvailability} disabled={!form.startDate || !form.endDate || checking} className="ebw-btn ebw-btn-primary" style={{ marginTop: 24 }} data-testid="check-availability-btn">
                  {checking ? <Loader2 className="ebw-spin" style={{ width: 18, height: 18 }} /> : "Search Available Rooms"}
                </button>
                <div className="ebw-trust" style={{ marginTop: 16 }}><Shield style={{ width: 12, height: 12 }} /> Best Rate Guarantee &middot; Secure Booking</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ borderRadius: 6, overflow: "hidden", flex: 1, minHeight: 240, background: "#E5E4DF" }}>
                  <img src="https://images.unsplash.com/photo-1729707691048-722c1acf5c51?w=800&q=80" alt="Resort" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </div>
                <div className="ebw-card" style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "1.5px", color: "#C2A878", marginBottom: 8 }}>Why Book Direct</div>
                  <div style={{ fontSize: 13, color: "#575757", lineHeight: 1.7 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}><Check style={{ width: 14, height: 14, color: "#3B5249" }} /> Best rate guarantee</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}><Check style={{ width: 14, height: 14, color: "#3B5249" }} /> Flexible cancellation</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Check style={{ width: 14, height: 14, color: "#3B5249" }} /> Direct communication with property</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 1: Room Selection */}
          {step === 1 && (
            <div data-testid="step-rooms">
              <button onClick={() => { setStep(0); setPriceBreakdown(null); }} className="ebw-back" data-testid="back-to-dates"><ChevronLeft style={{ width: 16, height: 16 }} /> Change dates</button>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <h2 className="ebw-card-title" style={{ margin: 0 }}>Select Your Room</h2>
                <span style={{ fontSize: 12, color: "#575757", background: "#F5F4F0", padding: "6px 14px", borderRadius: 4 }}>{nightCount} night{nightCount !== 1 ? "s" : ""} &middot; {form.startDate} to {form.endDate}</span>
              </div>
              <div className="ebw-2col">
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {rooms.length === 0 ? (
                    <div className="ebw-card" style={{ textAlign: "center", padding: 48 }}>
                      <BedDouble style={{ width: 40, height: 40, color: "#E5E4DF", margin: "0 auto 12px" }} />
                      <p style={{ fontWeight: 600 }}>No rooms available for these dates</p>
                      <p style={{ fontSize: 13, color: "#575757" }}>Please try different dates</p>
                    </div>
                  ) : rooms.map(room => {
                    const roomId = String(room.roomTypeID || room.roomTypeId || room.id);
                    const rate = rates[roomId];
                    const totalRate = rate?.totalRate || null;
                    const perNight = totalRate ? (parseFloat(totalRate) / nightCount) : (rate?.ratePerNight || room.rate || null);
                    const isSelected = form.roomTypeId === roomId;
                    const imgData = roomImages[roomId] || {};
                    const photos = imgData.photos || room.roomTypePhotos || [];
                    const idx = activePhoto[roomId] || 0;
                    const badges = imgData.badges || [];
                    const desc = imgData.description || room.roomTypeDescription?.replace(/<[^>]+>/g, ' ').trim().substring(0, 150) || "";
                    return (
                      <div key={roomId} className={`ebw-room${isSelected ? " selected" : ""}`} onClick={() => selectRoom(roomId, room.roomTypeName || room.name || "Room")} data-testid={`room-option-${roomId}`}>
                        {photos.length > 0 && (
                          <div className="ebw-room-img">
                            <img src={photos[idx]} alt={room.roomTypeName || "Room"} loading="lazy" />
                            {photos.length > 1 && (<>
                              <button className="ebw-room-nav left" onClick={e => { e.stopPropagation(); setActivePhoto(p => ({ ...p, [roomId]: (idx - 1 + photos.length) % photos.length })); }}><ChevronLeft style={{ width: 16, height: 16 }} /></button>
                              <button className="ebw-room-nav right" onClick={e => { e.stopPropagation(); setActivePhoto(p => ({ ...p, [roomId]: (idx + 1) % photos.length })); }}><ChevronRight style={{ width: 16, height: 16 }} /></button>
                              <div className="ebw-room-dots">{photos.slice(0, 5).map((_, i) => <div key={i} className={`ebw-room-dot${i === idx ? " active" : ""}`} />)}</div>
                            </>)}
                            {perNight && <div className="ebw-room-price">{sym}{parseFloat(perNight).toFixed(0)}<small>/night</small></div>}
                            {badges.length > 0 && <div className="ebw-room-badges">{badges.map((b, i) => <span key={i} className="ebw-room-badge">{b}</span>)}</div>}
                          </div>
                        )}
                        <div className="ebw-room-info">
                          <h3 className="ebw-room-name">{room.roomTypeName || room.name}</h3>
                          {desc && <p className="ebw-room-desc">{desc}</p>}
                          <div className="ebw-room-meta">
                            {(room.maxGuests || room.adultsIncluded) && <span>Up to {room.maxGuests || room.adultsIncluded} guests</span>}
                            {room.roomsAvailable != null && <span className={room.roomsAvailable <= 2 ? "ebw-room-avail" : ""}>{room.roomsAvailable <= 2 ? `Only ${room.roomsAvailable} left` : `${room.roomsAvailable} available`}</span>}
                          </div>
                          {isSelected && <div className="ebw-room-selected"><Check style={{ width: 14, height: 14 }} /> Selected {calculatingPrice && <Loader2 className="ebw-spin" style={{ width: 14, height: 14 }} />}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div>
                  <PriceSummaryCard />
                  {form.roomTypeId && (
                    <button onClick={() => { setStep(2); if (form.email) createHold(); }} className="ebw-btn ebw-btn-primary" style={{ marginTop: 16 }} data-testid="continue-to-details-btn">Continue</button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Guest Details */}
          {step === 2 && (
            <div className="ebw-2col" data-testid="step-guest">
              <div>
                <button onClick={() => setStep(1)} className="ebw-back"><ChevronLeft style={{ width: 16, height: 16 }} /> Back to rooms</button>
                <div className="ebw-card">
                  <h2 className="ebw-card-title">Guest Information</h2>
                  <div className="ebw-grid2">
                    <div><label className="ebw-label">First Name *</label><input type="text" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} className="ebw-input" placeholder="John" data-testid="guest-first-name" /></div>
                    <div><label className="ebw-label">Last Name *</label><input type="text" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} className="ebw-input" placeholder="Doe" data-testid="guest-last-name" /></div>
                  </div>
                  <div style={{ marginTop: 16 }}><label className="ebw-label">Email Address *</label>
                    <div className="ebw-input-icon"><Mail /><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} onBlur={() => { if (form.email && !holdId) createHold(); }} className="ebw-input" placeholder="john@example.com" data-testid="guest-email" /></div>
                  </div>
                  {settings.show_phone !== false && (
                    <div style={{ marginTop: 16 }}><label className="ebw-label">Phone</label>
                      <div className="ebw-input-icon"><Phone /><input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="ebw-input" placeholder="+1 (555) 000-0000" data-testid="guest-phone" /></div>
                    </div>
                  )}
                  <div style={{ marginTop: 16 }}><label className="ebw-label">Special Requests</label>
                    <textarea value={form.specialRequests} onChange={e => setForm(f => ({ ...f, specialRequests: e.target.value }))} className="ebw-textarea" rows={3} placeholder="Early check-in, dietary requirements..." data-testid="guest-special-requests" />
                  </div>
                  <button onClick={() => setStep(3)} disabled={!form.firstName || !form.lastName || !form.email} className="ebw-btn ebw-btn-primary" style={{ marginTop: 24 }} data-testid="continue-to-confirm-btn">Review Booking</button>
                </div>
              </div>
              <div><PriceSummaryCard /></div>
            </div>
          )}

          {/* STEP 3: Confirm */}
          {step === 3 && (
            <div className="ebw-2col" data-testid="step-confirm">
              <div>
                <button onClick={() => setStep(2)} className="ebw-back"><ChevronLeft style={{ width: 16, height: 16 }} /> Back</button>
                <div className="ebw-card">
                  <h2 className="ebw-card-title">Booking Summary</h2>
                  <div className="ebw-summary-row"><span>Property</span><span>{config?.property_name}</span></div>
                  <div className="ebw-summary-row"><span>Room</span><span>{form.roomTypeName}</span></div>
                  <div className="ebw-summary-row"><span>Check-in</span><span>{form.startDate}</span></div>
                  <div className="ebw-summary-row"><span>Check-out</span><span>{form.endDate}</span></div>
                  <div className="ebw-summary-row"><span>Duration</span><span>{nightCount} night{nightCount !== 1 ? "s" : ""}</span></div>
                  <div className="ebw-summary-row"><span>Guests</span><span>{form.adults} adult{form.adults !== 1 ? "s" : ""}{form.children > 0 ? `, ${form.children} child${form.children !== 1 ? "ren" : ""}` : ""}</span></div>
                  <div className="ebw-summary-row" style={{ borderTop: "1px solid #E5E4DF", marginTop: 8, paddingTop: 12 }}><span>Guest</span><span>{form.firstName} {form.lastName}</span></div>
                  <div className="ebw-summary-row"><span>Email</span><span style={{ fontSize: 13 }}>{form.email}</span></div>
                  {form.phone && <div className="ebw-summary-row"><span>Phone</span><span>{form.phone}</span></div>}
                  {bookingSettings.cancellation_policy && (
                    <div className="ebw-policy"><FileText /><div><strong style={{ color: "#1A2421" }}>Cancellation Policy</strong><br />{bookingSettings.cancellation_policy}</div></div>
                  )}
                </div>
                <button onClick={submitBooking} disabled={booking} className="ebw-btn ebw-btn-accent" style={{ marginTop: 16 }} data-testid="confirm-booking-btn">
                  {booking ? <Loader2 className="ebw-spin" style={{ width: 18, height: 18 }} /> : `Confirm Booking${priceBreakdown ? ` — ${sym}${priceBreakdown.amount_due.toFixed(2)}` : ""}`}
                </button>
                <div className="ebw-trust" style={{ marginTop: 12 }}><Shield style={{ width: 12, height: 12 }} /> Secure booking powered by CloudSync Pro</div>
              </div>
              <div><PriceSummaryCard /></div>
            </div>
          )}

          {/* STEP 4: PCI-Compliant Receipt */}
          {step === 4 && bookingResult && (() => {
            const pay = bookingResult.payment || {};
            const statusText = pay.approval_code ? "APPROVED" : "Confirmed";
            const statusColor = pay.approval_code ? "#059669" : "#C2A878";
            const totalAmount = priceBreakdown ? priceBreakdown.grand_total : (pay.amount ? parseFloat(pay.amount) : 0);
            const guestName = bookingResult.guest_name || `${form.firstName} ${form.lastName}`;
            const checkIn = bookingResult.check_in || form.startDate;
            const checkOut = bookingResult.check_out || form.endDate;
            const nights = bookingResult.nights || nightCount;
            return (
            <div style={{ maxWidth: 600, margin: "0 auto" }} data-testid="step-success">
              {/* Green Success Header */}
              <div style={{ background: statusColor, borderRadius: "6px 6px 0 0", padding: "28px 24px", textAlign: "center", color: "#fff" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                  <Check style={{ width: 28, height: 28 }} />
                </div>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, fontFamily: "'Cormorant Garamond',serif" }}>Payment Successful</h2>
                <p style={{ fontSize: 28, fontWeight: 800, margin: "8px 0 4px" }}>{sym}{totalAmount.toFixed(2)}</p>
                <p style={{ fontSize: 13, opacity: 0.8 }}>{new Date().toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
              </div>

              {/* Order Info 2-Column */}
              <div style={{ border: "1px solid #E5E4DF", borderTop: "none", background: "#fff", padding: 24 }}>
                <div className="ebw-grid2">
                  <div><span className="ebw-label">Order ID</span><p style={{ fontFamily: "monospace", fontWeight: 600, fontSize: 13, margin: 0 }} data-testid="booking-ref">{pay.order_id || bookingResult.booking_ref}</p></div>
                  <div><span className="ebw-label">Date & Time</span><p style={{ fontWeight: 600, fontSize: 13, margin: 0 }}>{new Date().toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p></div>
                </div>
                <div className="ebw-grid2" style={{ marginTop: 16 }}>
                  <div><span className="ebw-label">Customer</span><p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>{guestName}</p><p style={{ fontSize: 12, color: "#999", margin: 0 }}>{form.email}</p></div>
                  <div><span className="ebw-label">Amount</span><p style={{ fontWeight: 800, fontSize: 20, margin: 0, color: "#1A2421" }}>{sym}{totalAmount.toFixed(2)}</p></div>
                </div>
              </div>

              {/* Booking Items */}
              <div style={{ border: "1px solid #E5E4DF", borderTop: "none", background: "#fff", padding: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #E5E4DF" }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{form.roomTypeName || "Room Booking"}</span>
                    <span style={{ display: "block", fontSize: 12, color: "#575757" }}>{checkIn} to {checkOut} ({nights} night{nights !== 1 ? "s" : ""})</span>
                  </div>
                  <span style={{ fontWeight: 600 }}>{priceBreakdown ? `${sym}${priceBreakdown.subtotal.toFixed(2)}` : ""}</span>
                </div>
                {priceBreakdown && priceBreakdown.tax_fee_lines.map((l, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13, color: "#575757" }}>
                    <span>{l.label}{l.type === "percentage" ? ` (${l.rate}%)` : ""}</span>
                    <span>{sym}{l.amount.toFixed(2)}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0 0", marginTop: 8, borderTop: "2px solid #1A2421", fontWeight: 800, fontSize: 16 }}>
                  <span>Total</span>
                  <span style={{ color: statusColor }}>{sym}{totalAmount.toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Transaction Details — PCI Compliant */}
              <div style={{ border: "1px solid #E5E4DF", borderTop: "none", borderRadius: "0 0 6px 6px", background: "#f0f9ff", padding: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <CreditCard style={{ width: 18, height: 18, color: "#1A2421" }} />
                  <span style={{ fontWeight: 700, fontSize: 15, color: "#1A2421" }}>Payment Transaction Details</span>
                </div>

                <div className="ebw-grid2" style={{ gap: 16 }}>
                  <div>
                    <span className="ebw-label">Transaction Status</span>
                    <p style={{ fontWeight: 800, fontSize: 16, margin: 0, color: statusColor }}>{statusText}</p>
                  </div>
                  <div>
                    <span className="ebw-label">Approval Code</span>
                    <p style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14, margin: 0 }}>{pay.approval_code || "—"}</p>
                  </div>
                </div>

                <div className="ebw-grid2" style={{ gap: 16, marginTop: 16 }}>
                  <div>
                    <span className="ebw-label">Card</span>
                    <p style={{ fontWeight: 800, fontSize: 16, margin: 0 }}>
                      {pay.card_brand || "CARD"} {pay.card_last4 ? `****${pay.card_last4}` : ""}
                    </p>
                  </div>
                  <div>
                    <span className="ebw-label">Transaction ID</span>
                    <p style={{ fontFamily: "monospace", fontSize: 12, margin: 0, wordBreak: "break-all" }}>{pay.transaction_id || "—"}</p>
                  </div>
                </div>

                <div className="ebw-grid2" style={{ gap: 16, marginTop: 16 }}>
                  <div>
                    <span className="ebw-label">Reference #</span>
                    <p style={{ fontFamily: "monospace", fontSize: 13, margin: 0 }}>{pay.ref_number || "—"}</p>
                  </div>
                  <div>
                    <span className="ebw-label">Payment Method</span>
                    <p style={{ fontSize: 14, margin: 0, fontWeight: 600 }}>Fiserv IPG</p>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 12, fontSize: 10, color: "#999", textAlign: "center", lineHeight: 1.6 }}>
                This receipt complies with PCI DSS standards. Full card numbers are never stored or displayed.<br />
                Payment processed securely via Fiserv IPG. Powered by CloudSync Pro.
              </div>

              <button onClick={() => { setStep(0); setBookingResult(null); setPriceBreakdown(null); setHoldId(null); setHoldExpiry(null); setForm({ startDate: "", endDate: "", roomTypeId: "", roomTypeName: "", adults: 2, children: 0, firstName: "", lastName: "", email: "", phone: "", specialRequests: "" }); }} className="ebw-btn ebw-btn-outline" style={{ marginTop: 16 }} data-testid="book-another-btn">Make Another Booking</button>
            </div>
            );
          })()}
        </div>

        <div className="ebw-footer">Powered by <a href="https://csyncpro.com" target="_blank" rel="noopener noreferrer">CloudSync Pro</a></div>
      </div>
    </>
  );
}
